export interface LocationCoords {
    lat: number;
    lng: number;
}

export type LocationErrorType = 'unsupported' | 'denied' | 'unavailable' | 'timeout' | 'unknown';

export interface LocationResult {
    success: boolean;
    coords?: LocationCoords;
    errorType?: LocationErrorType;
    message?: string;
}

/**
 * Wraps navigator.geolocation.getCurrentPosition in a Promise
 */
const getPosition = (options?: PositionOptions): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
};

/**
 * Attempts to get the current user location with robust error handling and retries.
 */
export const getCurrentLocation = async (): Promise<LocationResult> => {
    if (!navigator.geolocation) {
        return { success: false, errorType: 'unsupported', message: 'Geolocation is not supported by your browser.' };
    }

    try {
        const position = await getPosition({
            timeout: 5000,
            maximumAge: 60000
        });

        return {
            success: true,
            coords: {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            }
        };
    } catch (error: any) {
        console.error("Error getting location:", error);

        // Handle Transient Errors (TIMEOUT or POSITION_UNAVAILABLE)
        // Code 1 = PERMISSION_DENIED
        // Code 2 = POSITION_UNAVAILABLE
        // Code 3 = TIMEOUT
        if (error.code === 2 || error.code === 3) {
            try {
                // Check if we actually have permission
                if (navigator.permissions && navigator.permissions.query) {
                    const permissionStatus = await navigator.permissions.query({ name: 'geolocation' as PermissionName });

                    if (permissionStatus.state === 'granted') {
                        console.log("Permission granted but location failed. Retrying one more time...");
                        // Retry once with fresh constraints
                        try {
                            const position = await getPosition({
                                timeout: 20000, // Increased timeout
                                maximumAge: 0 // Force fresh
                            });
                            return {
                                success: true,
                                coords: {
                                    lat: position.coords.latitude,
                                    lng: position.coords.longitude
                                }
                            };
                        } catch (retryError) {
                            console.error("Retry failed:", retryError);
                            return { success: false, errorType: 'unavailable', message: 'Unable to determine your location. Please check your signal.' };
                        }
                    } else {
                         return { success: false, errorType: 'timeout', message: 'Location request timed out. Please try again.' };
                    }
                } else {
                     // Permissions API not supported, return generic timeout
                     return { success: false, errorType: 'timeout', message: 'Location request timed out. Please try again.' };
                }
            } catch (permError) {
                console.error("Permissions check failed:", permError);
                return { success: false, errorType: 'unavailable', message: 'Location unavailable.' };
            }
        } else if (error.code === 1) {
            return { success: false, errorType: 'denied', message: 'Location access denied. Defaulting to London. Enable location for the best experience.' };
        }
        
        return { success: false, errorType: 'unknown', message: 'An unknown error occurred while fetching location.' };
    }
};
